import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Activity, History, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getTodayString } from '@/lib/dateUtils';
import { getPlatoDoctorName } from '@/lib/platoDoctors';

// ---------------------------------------------------------------------------
// PatientVisitHistory
//
// Compact "Visit history (Plato)" panel for a single patient case. Reads the
// patient's Plato appointment journey (last 12 months + upcoming) via the
// plato-patient-visits Edge Function, plus a live "in clinic today" queue
// banner when the patient is physically in the clinic right now.
//
// Renders a one-line muted note when the case has no real Plato patient
// linked yet (null / empty / PENDING- placeholder reference).
// ---------------------------------------------------------------------------

interface PlatoVisit {
  date: string;
  time: string;
  doctor: string;
  title: string;
  on_waitlist: boolean;
}

interface PlatoTodayQueue {
  status_name: string;
  location: string;
  appt_time: string;
}

interface PlatoPatientVisitsResponse {
  success: boolean;
  error?: string;
  visits?: PlatoVisit[];
  today_queue?: PlatoTodayQueue | null;
}

interface PatientVisitHistoryProps {
  platoReferenceId: string | null;
}

const fetchPatientVisits = async (
  platoReferenceId: string
): Promise<{ visits: PlatoVisit[]; todayQueue: PlatoTodayQueue | null }> => {
  const { data, error } = await supabase.functions.invoke('plato-patient-visits', {
    body: { patient_id: platoReferenceId },
  });

  if (error) {
    throw new Error(`Plato visit history error: ${error.message}`);
  }

  const payload = data as PlatoPatientVisitsResponse | null;
  if (!payload?.success) {
    throw new Error(payload?.error || 'Failed to load visit history from Plato');
  }

  return {
    visits: payload.visits ?? [],
    todayQueue: payload.today_queue ?? null,
  };
};

const formatVisitDate = (value: string): string => {
  try {
    return format(parseISO(value), 'd MMM yyyy');
  } catch {
    return value;
  }
};

const PatientVisitHistory: React.FC<PatientVisitHistoryProps> = ({ platoReferenceId }) => {
  const trimmedId = (platoReferenceId ?? '').trim();
  const hasPlatoId = Boolean(trimmedId) && !trimmedId.startsWith('PENDING-');

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['plato-patient-visits', trimmedId],
    queryFn: () => fetchPatientVisits(trimmedId),
    enabled: hasPlatoId,
  });

  if (!hasPlatoId) {
    return <p className="text-sm text-muted-foreground">No Plato patient linked yet.</p>;
  }

  const visits = data?.visits ?? [];
  const todayQueue = data?.todayQueue ?? null;
  const todayString = getTodayString();

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <History className="h-4 w-4 text-slate-500" />
            Visit history (Plato)
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            title="Refresh visit history"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {todayQueue && (
          <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
            <Activity className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <p className="text-sm text-emerald-900">
              In clinic today: {todayQueue.status_name} at {todayQueue.location} (
              {todayQueue.appt_time})
            </p>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading visits from Plato...
          </div>
        )}

        {isError && (
          <p className="py-2 text-sm text-rose-700">
            {error instanceof Error ? error.message : 'Could not load visit history.'}
          </p>
        )}

        {!isLoading && !isError && visits.length === 0 && (
          <p className="py-2 text-sm text-muted-foreground">No Plato visits found</p>
        )}

        {!isLoading && !isError && visits.length > 0 && (
          <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
            {visits.map((visit, index) => (
              <div
                key={`${visit.date}-${visit.time}-${index}`}
                className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="font-medium text-slate-900">
                    {formatVisitDate(visit.date)}
                  </span>
                  {visit.time && <span className="text-slate-600">{visit.time}</span>}
                  {visit.on_waitlist && (
                    <Badge
                      variant="outline"
                      className="border-slate-200 bg-slate-100 px-1.5 py-0 text-[10px] font-normal text-slate-500"
                    >
                      waitlist
                    </Badge>
                  )}
                  {visit.date > todayString && (
                    <Badge
                      variant="outline"
                      className="border-sky-200 bg-sky-50 px-1.5 py-0 text-[10px] font-normal text-sky-700"
                    >
                      upcoming
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {getPlatoDoctorName(visit.doctor)}
                  {visit.title && <span> &middot; {visit.title}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientVisitHistory;
